/**
 * Apple Account Migration Page
 * 
 * Handles migrating an old Apple account to a new one after developer account switch.
 * URL: /migrate?code=xxx (code is always required)
 * 
 * Flow:
 * 1. Code must be present in URL - if missing, show error
 * 2. If signed in: Validate code matches account's mergePassword
 *    - Match: Show Apple Sign in button
 *    - Mismatch: Show "Merge into this account" option
 * 3. If signed out: Show Apple Sign in button
 * 4. After successful sign-in (or merge confirmation), execute the merge
 * 5. On success, redirect to dashboard
 */

import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, XCircle, ArrowRight, AlertTriangle } from "lucide-react";
import { useMergeAccount, useProfile } from "@/lib/api/useProfile";
import { useAuth, useAppNavigation, AuthActionsContext } from "@/lib/platform";
import logo from "@/assets/icons/outclose.svg";
import tracking from "@/lib/tracking";

type MigrationState = "loading" | "idle" | "mismatch" | "merging" | "success" | "error";

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

export default function Migrate() {
  const { navigate } = useAppNavigation();
  const { isSignedIn, isLoaded: authLoaded, user } = useAuth();
  const actions = useContext(AuthActionsContext);
  const mergeAccount = useMergeAccount();
  const { data: profileData, isLoading: profileLoading } = useProfile();
  
  const [state, setState] = useState<MigrationState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [migrationCompleted, setMigrationCompleted] = useState(false);
  const hasTrackedPageView = useRef(false);

  // Extract code from URL - always required
  const urlCode = new URLSearchParams(window.location.search).get("code");

  // Determine page state based on auth and URL code
  useEffect(() => {
    if (!authLoaded) return;
    if (migrationCompleted) return;

    // Code is always required in the URL
    if (!urlCode) {
      const errorMsg = "No migration code provided. Please use the migration link sent to you.";
      setError(errorMsg);
      setState("error");
      tracking.migrationError(errorMsg);
      return;
    }

    if (isSignedIn) {
      // Signed in: wait for profile to load, then validate code matches account
      if (profileLoading) return;

      const profileMigration = profileData?.data as any;
      const backendPassword = profileMigration?.mergePassword;

      if (backendPassword === urlCode) {
        // Code matches current account - show Apple Sign in to re-auth
        setState("idle");
        if (!hasTrackedPageView.current) {
          tracking.migrationPageViewed(true, true);
          hasTrackedPageView.current = true;
        }
      } else {
        // Code doesn't match (or no mergePassword) - offer to merge into current account
        setState("mismatch");
        if (!hasTrackedPageView.current) {
          tracking.migrationPageViewed(true, true);
          hasTrackedPageView.current = true;
        }
      }
    } else {
      // Signed out: show Apple Sign in button
      setState("idle");
      if (!hasTrackedPageView.current) {
        tracking.migrationPageViewed(true, false);
        hasTrackedPageView.current = true;
      }
    }
  }, [authLoaded, isSignedIn, profileLoading, profileData, urlCode, migrationCompleted]);

  const performMerge = useCallback(async () => {
    if (!urlCode) return;
    
    setState("merging");
    setError(null);
    tracking.migrationStarted();
    
    try {
      console.log('Performing merge with code:', urlCode);
      const result = await mergeAccount.mutateAsync(urlCode);
      
      if (result.success) {
        setMigrationCompleted(true);
        setState("success");
        tracking.migrationSuccess();
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        const errorMsg = result.error || "Failed to merge account";
        setState("error");
        setError(errorMsg);
        tracking.migrationError(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err?.message || "An unexpected error occurred";
      setState("error");
      setError(errorMsg);
      tracking.migrationError(errorMsg);
    }
  }, [urlCode, mergeAccount, navigate]);

  const handleAppleSignIn = async () => {
    if (!actions) {
      setError("Sign-in not available");
      return;
    }
    
    setError(null);
    tracking.migrationAppleSignInClicked();
    
    try {
      // Sign in returns the user on success, null on failure/cancellation
      const signedInUser = await actions.signInWithApple();
      
      if (signedInUser) {
        console.log('Signed in user:', signedInUser?.email, signedInUser?.id);
        setTimeout(() => {
          // Sign-in succeeded, immediately perform the merge
          performMerge();
        }, 500);
      }
      // If null, user cancelled or there was an error (already handled by AuthProvider)
    } catch (err: any) {
      // Only show error for non-cancellation errors
      const errorMsg = err?.message?.toLowerCase() || "";
      const isCancellation = errorMsg.includes("cancelled") || 
                            errorMsg.includes("canceled") || 
                            errorMsg.includes("closed") ||
                            errorMsg.includes("popup") ||
                            err?.code === "auth/popup-closed-by-user";
      
      if (!isCancellation) {
        const signInError = "Failed to sign in with Apple. Please try again.";
        setError(signInError);
        tracking.migrationError(signInError);
      }
    }
  };

  // Show loading while determining state
  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <img src={logo} alt="Outclose" className="w-10 h-10 invert" />
            </div>
          </div>

          {/* Content based on state */}
          {state === "success" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-4"
            >
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Account Merged!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Your account data has been successfully migrated. Redirecting you now...
              </p>
              <div className="flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              </div>
            </motion.div>
          ) : state === "error" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-4"
            >
              <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                {error?.includes("different account") ? (
                  <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {error?.includes("different account") ? "Wrong Account" : "Migration Failed"}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {error || "Something went wrong. Please contact support."}
              </p>
              <button
                onClick={() => {
                  tracking.migrationGoToDashboardClicked();
                  navigate("/");
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-xl transition-colors"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ) : state === "merging" ? (
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Migrating Your Account
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we transfer your data...
              </p>
            </div>
          ) : state === "mismatch" ? (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Account Migration
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  You're signed in as <span className="font-medium text-gray-900 dark:text-white">{user?.email}</span>. Would you like to merge your old account data into this one?
                </p>
              </div>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={performMerge}
                disabled={mergeAccount.isPending}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Merge into this account
                {mergeAccount.isPending && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700" />
                <span className="text-sm text-gray-500 dark:text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700" />
              </div>

              <button
                onClick={handleAppleSignIn}
                disabled={actions?.isLoading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-black dark:bg-white text-white dark:text-black font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AppleLogo className="w-5 h-5" />
                Sign in with Apple
                {actions?.isLoading && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
              </button>

              <button
                onClick={() => navigate("/")}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Account Migration
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  We've updated our Apple Sign-In and need you to reauthorize. Please sign in with your Apple account to migrate your existing data.
                </p>
              </div>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <p className="text-center text-sm text-gray-500 dark:text-gray-500">
                If you run into any issues, please contact support at <a href="mailto:team@outclose.com" className="text-primary-600 hover:text-primary-500">team@outclose.com</a>.
              </p>

              <button
                onClick={handleAppleSignIn}
                disabled={actions?.isLoading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-black dark:bg-white text-white dark:text-black font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AppleLogo className="w-5 h-5" />
                Sign in with Apple
                {actions?.isLoading && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
              </button>
              <p className="text-center text-sm text-gray-500 dark:text-gray-500">
                Your bookings, subscription, and profile data will be preserved.
              </p>

              
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
